import Vue from 'vue';
import isArray from '../../../utils/isArray';
import DataEvent from './data-event';
import { observe } from '../infrastructure/observable';
import type DataRecord from './data-record';
import _ from '../../../utils';
import { isDataRecord } from './data-record';
import deepClone from '../../../utils/deepClone';

export interface IDataSourcePaging {
  pageSize?: number;
  currentPage?: number;
  totalPages?: number;
  totalCount?: number;
}

// Adjust this in case Backend props change,
// should only be used internally for mapping, so no export
interface IBackendPaging {
  pageSize?: number;
  currentPage?: number;
  totalPages?: number;
  totalCount?: number;
  includeCount?: boolean;
}

export type DataSourceFilter<TFilter> = Omit<TFilter, 'paging'>;

export type DataSourceSelectCommand<TReturn = any, TRequest = any, TArgs extends unknown[] = []> = ((request: TRequest, ...args: Partial<TArgs>) => Promise<TReturn>);
export type DataSourceUpdateCommand<TReturn = any, TRequest = any, TArgs extends unknown[] = []> = ((request: TRequest, ...args: Partial<TArgs>) => Promise<TReturn>);
export type DataSourceInsertCommand<TReturn = any, TRequest = any, TArgs extends unknown[] = []> = ((request: TRequest, ...args: Partial<TArgs>) => Promise<TReturn>);
export type DataSourceDeleteCommand<TReturn = any, TRequest = any, TArgs extends unknown[] = []> = ((request: TRequest, ...args: Partial<TArgs>) => Promise<TReturn>);

export type UnpackDataRecord<T> = T extends DataRecord ? T['data'] : T;
/**
* In case of a list result, unpack the type of a single entry, otherwise return the type as is
* */
export type UnpackListType<T> = T extends {items: any[]} ? T['items'][0] : T;
/**
 * Unpack the type of a DataRecord, in case of a list the type of a single item is unpacked
 */
export type UnpackDataType<T> = UnpackDataRecord<UnpackListType<UnpackDataRecord<T>>>;

export type DataSourceConfig<TSelectReturn = any, TSelectRequest = any, TSelectArgs extends unknown[] = unknown[], TUpdateReturn = any, TUpdateRequest = any, TUpdateArgs extends unknown[] = unknown[], TInsertReturn = any, TInsertRequest = any, TInsertArgs extends unknown[] = unknown[], TDeleteReturn = any, TDeleteRequest = any, TDeleteArgs extends unknown[] = unknown[]> = {
  selectCommand?: DataSourceSelectCommand<TSelectReturn, TSelectRequest, TSelectArgs>,
  updateCommand?: DataSourceUpdateCommand<TUpdateReturn, TUpdateRequest, TUpdateArgs>,
  insertCommand?: DataSourceInsertCommand<TInsertReturn, TInsertRequest, TInsertArgs>,
  deleteCommand?: DataSourceDeleteCommand<TDeleteReturn, TDeleteRequest, TDeleteArgs>,
  noRefreshOnFilterChange?: boolean,
};

export default class DataSource<TSelectReturn = any, TSelectRequest = any, TSelectArgs extends unknown[] = unknown[], TUpdateReturn = any, TUpdateRequest = any, TUpdateArgs extends unknown[] = unknown[], TInsertReturn = any, TInsertRequest = any, TInsertArgs extends unknown[] = unknown[], TDeleteReturn = any, TDeleteRequest extends { id?: any } = any, TDeleteArgs extends unknown[] = unknown[]> {
  private _filter: DataSourceFilter<TSelectRequest>;
  private _config: DataSourceConfig<TSelectReturn, TSelectRequest, TSelectArgs, TUpdateReturn, TUpdateRequest, TUpdateArgs, TInsertReturn, TInsertRequest, TInsertArgs, TDeleteReturn, TDeleteRequest, TDeleteArgs>;
  private _paging: IDataSourcePaging = {
    currentPage: undefined,
    pageSize: undefined,
    totalCount: undefined,
    totalPages: undefined,
  };

  selectCommand?: DataSourceSelectCommand<TSelectReturn, TSelectRequest, TSelectArgs>;
  updateCommand?: DataSourceUpdateCommand<TUpdateReturn, TUpdateRequest, TUpdateArgs>;
  insertCommand?: DataSourceInsertCommand<TInsertReturn, TInsertRequest, TInsertArgs>;
  deleteCommand?: DataSourceDeleteCommand<TDeleteReturn, TDeleteRequest, TDeleteArgs>;

  onSelecting = new DataEvent();
  onSelected = new DataEvent<[TSelectReturn]>();
  onUpdating = new DataEvent();
  onUpdated = new DataEvent<[TUpdateReturn]>();
  onInserting = new DataEvent();
  onInserted = new DataEvent<[TInsertReturn]>();
  onDeleting = new DataEvent();
  onDeleted = new DataEvent<[TDeleteReturn]>();

  data?: TSelectReturn;
  isLoading: boolean = false;
  error: any = undefined;

  onError = new DataEvent<[any]>();

  get filter() { return this._filter; }
  set filter(value) {
    this._filter = observe(value, this.onFilterChange, true);
  }

  get paging() { return this._paging; }
  set paging(value) {
    this._paging = observe(value, this.onPagingChange, true);
  }

  /** Setup data source commands with arrow functions */
  constructor(config: DataSourceConfig<TSelectReturn, TSelectRequest, TSelectArgs, TUpdateReturn, TUpdateRequest, TUpdateArgs, TInsertReturn, TInsertRequest, TInsertArgs, TDeleteReturn, TDeleteRequest, TDeleteArgs>) {
    this._config = config;
    this._filter = observe({}, this.onFilterChange, false) as DataSourceFilter<TSelectRequest>;
    this.selectCommand = this._config.selectCommand;
    this.updateCommand = this._config.updateCommand;
    this.insertCommand = this._config.insertCommand;
    this.deleteCommand = this._config.deleteCommand;
  }

  private async performCommand<TCommandData, TArgs extends unknown[]>(commandFunction: ((...args: TArgs) => TCommandData | undefined) | undefined, ...args: TArgs): Promise<TCommandData | undefined> {
    this.error = undefined;
    try {
      // DataSources should always use arrow functions because of Javascript 'this' scope nonsense
      // https://www.codementor.io/@dariogarciamoya/understanding-this-in-javascript-with-arrow-functions-gcpjwfyuc
      if (typeof commandFunction === 'function') {
        let res = commandFunction(...args);
        if (res === undefined || res === null) {
          return undefined;
        }
        if (_.isPromise(res)) {
          res = await res;
        }
        return res;
      }
      throw new Error('Unable to select data for DataSource, the provided data command was not a function');
    } catch (error: any) {
      this.error = error;
      this.onError.invoke(error);
      throw error;
    }
  }

  async select(): Promise<TSelectReturn>;
  async select(...args: Partial<TSelectArgs>): Promise<TSelectReturn>;
  async select(...args: Partial<TSelectArgs>): Promise<TSelectReturn> {
    this.isLoading = true;
    this.onSelecting.invoke();
    // Clone in order to lose the observers and to prevent mutation of the filter
    const filter = deepClone(this._filter) as {paging?: IBackendPaging};
    if (this._paging?.currentPage) {
      filter.paging = {};
      filter.paging.currentPage = this._paging.currentPage;
      filter.paging.pageSize = this._paging.pageSize;
      filter.paging.includeCount = true;
    }

    const resp = (await this.performCommand(this.selectCommand, filter as TSelectRequest, ...args)) as unknown as DataRecord<unknown> & {items?: DataRecord<unknown>[], paginationData?: IBackendPaging};

    const items = resp?.items;
    if (isArray(items)) {
      items.forEach((i) => {
        if (isDataRecord(i)) {
          // cast to any to set the private variable
          (i as any)._dataSource = this;
        }
      });
    } else if (isDataRecord(resp)) {
      // cast to any to set the private variable
      (resp as any)._dataSource = this;
    } // Else case = it's not a DataRecord so don't assign the DataSource

    // TODO: #8397 Get rid of Vue instance (Core Framework should have no references to Vue at all)
    // This is needed to make the data source reactive
    // It won't be necessary anymore in Vue V3
    Vue.set(this, 'data', resp);

    const paginationData = resp?.paginationData;
    if (paginationData?.currentPage
    && (
      this._paging.currentPage !== paginationData.currentPage
    || this._paging.pageSize !== (paginationData.pageSize ?? filter.paging?.pageSize)
    || this._paging.totalCount !== (paginationData.totalCount ?? filter.paging?.totalCount)
    || this._paging.totalPages !== (paginationData.totalPages ?? filter.paging?.totalPages))) {
      // This assumes that the backend paging structure does not change
      this._paging.currentPage = paginationData.currentPage;
      this._paging.pageSize = paginationData.pageSize ?? filter.paging?.pageSize;
      this._paging.totalCount = paginationData.totalCount ?? filter.paging?.totalCount;
      this._paging.totalPages = paginationData.totalPages ?? filter.paging?.totalPages;
    }

    this.isLoading = false;
    this.onSelected.invoke(this.data!);
    return this.data!;
  }

  async delete(id: {id: any} | string | number, ...args: Partial<TDeleteArgs>) {
    this.isLoading = true;
    this.onDeleting.invoke();
    const request = typeof id === 'object' ? id : { id };
    const res = await this.performCommand(this.deleteCommand, request as TDeleteRequest, ...args);
    this.isLoading = false;
    this.onDeleted.invoke(res!);
    return res!;
  }

  async update(request: TUpdateRequest, ...args: Partial<TUpdateArgs>) {
    this.isLoading = true;
    this.onUpdating.invoke();
    const res = await this.performCommand(this.updateCommand, request, ...args);
    this.isLoading = false;
    this.onUpdated.invoke(res!);
    return res!;
  }

  async insert(resource: TInsertRequest, ...args: Partial<TInsertArgs>) {
    this.isLoading = true;
    this.onInserting.invoke();
    const res = await this.performCommand(this.insertCommand, resource, ...args);
    this.isLoading = false;
    this.onInserted.invoke(res!);
    return res!;
  }

  // Must be an arrow function to preserve 'this' context
  private onFilterChange = () => {
    if (!this._config.noRefreshOnFilterChange) {
      // Wait a tick for all other code and propagations to catch up
      setTimeout(() => {
        this.select();
      }, 0);
    }
  };

  // Must be an arrow function to preserve 'this' context
  private onPagingChange = () => {
    if (!this._config.noRefreshOnFilterChange) {
      // Wait a tick for all other code and propagations to catch up
      setTimeout(() => {
        this.select();
      }, 0);
    }
  };

  // #region Deprecated
  /**
   * @deprecated A DataSource is always "initialized"
   * If you want to check if any data has been loaded simply check ```if(dataSource.data)``` instead
   */
  isInitialized: boolean = false;
  /**
   * @deprecated There is no need to initialize a DataSource, they are ready-to-go,
   * this can safely be replaced with a select call
  */
  async initialize() {
    await (this.select as any)();
    this.isInitialized = true;
    return this;
  }
  // #endregion
}
